import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { COLORS } from "../../constants";
import Chip from "../Chip/Chip";
import { toTitleCase } from "../../utils/formatting";

/**
 * OtherListCardCommon component for Parking, Bathrooms, Attractions renders a generic card with a title, subtitle, and optional Chip component.
 *
 * @param {object} props - The props passed to the component.
 * @param {JSX.Element} props.FontAwesomeIcon - The icon component to be used.
 * @param {JSX.Element} props.icon - The specific icon to display on the card (left section).
 * @param {JSX.Element} [props.chipIcon] - The specific icon to display inside the chip.
 * @param {string} props.title - The title text.
 * @param {string} props.subTitle - The subtitle text.
 * @param {string} [props.chipText] - Optional chip text to display.
 * @param {boolean} props.showChip - Whether to show the chip.
 * @param {string} [props.chipBackgroundColor] - The background color for the chip.
 * @param {string} [props.chipBorderColor] - The border color for the chip.
 * @param {string} [props.chipIconColor] - The icon color for the chip.
 * @param {number} [props.iconSize] - The size of the icon.
 *
 * @returns JSX.Element representing a styled card with icon, title, subtitle, and chip.
 */
const OtherListCardCommon = ({
  FontAwesomeIcon,
  icon,
  chipIcon,
  title,
  distance,
  subTitle,
  chipText,
  showChip,
  chipBackgroundColor,
  chipBorderColor,
  chipIconColor,
  iconSize,
  parkingIndicator,
}) => {
  return (
    <View style={styles.card}>
      {/* Left section for the icon */}
      <View style={styles.leftSection}>
        <View style={styles.iconCircle}>
          <FontAwesomeIcon icon={icon} size={iconSize} color={COLORS.textLight} />
        </View>
      </View>

      {/* Right section for title, subtitle, and optional chip */}
      <View style={styles.rightSection}>
        <Text style={styles.title}>{toTitleCase(title)}</Text>
        {parkingIndicator && parkingIndicator}
        {distance && <Text style={styles.subTitle}>{distance}</Text>}
        <Text style={[styles.subTitle, { marginBottom: showChip ? 8 : 0 }]}>{subTitle}</Text>

        {/* Conditionally render Chip if showChip is true */}
        {showChip && (
          <Chip
            FontAwesomeIcon={FontAwesomeIcon}
            icon={chipIcon || icon}
            title={chipText}
            size={iconSize}
            backgroundColor={chipBackgroundColor || COLORS.backgroundColor}
            borderColor={chipBorderColor || COLORS.border}
            iconColor={chipIconColor || COLORS.border}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    padding: 20,
    backgroundColor: COLORS.textLight,
    borderRadius: 8,
    marginBottom: 16,
    borderColor: COLORS.border,
    borderWidth: 0.5,
  },
  leftSection: {
    marginRight: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.secondary,
    justifyContent: "center",
    alignItems: "center",
  },
  rightSection: {
    flex: 1,
    justifyContent: "center",
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
    color: COLORS.textDarker,
    lineHeight: 28,
  },
  subTitle: {
    fontSize: 14,
    color: COLORS.border,
    marginTop: 4,
    lineHeight: 24,
  },
});

export default OtherListCardCommon;
